import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import Logo from "./Logo";
import { scrollToId, WA_DEFAULT } from "@/lib/site";

const LINKS = [
    { label: "Collections", href: "#collections" },
    { label: "Featured", href: "#featured" },
    { label: "Occasions", href: "#occasions" },
    { label: "Why Us", href: "#why" },
    { label: "How to Order", href: "#order" },
    { label: "Gallery", href: "#gallery" },
];

const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (href) => {
        setOpen(false);
        scrollToId(href);
    };

    return (
        <motion.header
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
                scrolled
                    ? "bg-[#F0EEE8]/85 backdrop-blur-md border-b border-[#472500]/10"
                    : "bg-transparent border-b border-transparent"
            }`}
            data-testid="main-nav"
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
                <button
                    onClick={() => go("#top")}
                    className="flex items-center gap-3 group"
                    data-testid="nav-home-link"
                    aria-label="Festive Theory — back to top"
                >
                    <Logo size={44} className="transition-transform duration-500 group-hover:rotate-[8deg]" />
                    <span className="font-script text-2xl text-[#472500] leading-none pt-1 hidden xs:inline sm:inline">
                        festive theory
                    </span>
                </button>

                <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
                    {LINKS.map((l) => (
                        <button
                            key={l.href}
                            onClick={() => go(l.href)}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="relative text-sm tracking-wide text-[#472500]/75 hover:text-[#472500] transition-colors duration-300 after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-[#E2BA03] after:transition-[width] after:duration-300 hover:after:w-full"
                        >
                            {l.label}
                        </button>
                    ))}
                    <a
                        href={WA_DEFAULT}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-whatsapp-button"
                        className="ml-2 inline-flex items-center gap-2 rounded-full bg-[#E2BA03] text-[#472500] text-sm font-semibold px-5 py-2.5 transition-transform duration-300 hover:scale-105 hover:-translate-y-0.5"
                    >
                        <MessageCircle size={16} strokeWidth={2.2} />
                        WhatsApp Us
                    </a>
                </nav>

                <button
                    className="lg:hidden text-[#472500] p-2"
                    onClick={() => setOpen(!open)}
                    data-testid="nav-mobile-menu-button"
                    aria-label={open ? "Close menu" : "Open menu"}
                >
                    {open ? <X size={26} /> : <Menu size={26} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:hidden overflow-hidden bg-[#F0EEE8]/95 backdrop-blur-md border-b border-[#472500]/10"
                        aria-label="Mobile"
                        data-testid="nav-mobile-menu"
                    >
                        <div className="px-6 py-6 flex flex-col gap-5">
                            {LINKS.map((l) => (
                                <button
                                    key={l.href}
                                    onClick={() => go(l.href)}
                                    data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="text-left font-display text-2xl text-[#472500]"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <a
                                href={WA_DEFAULT}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="nav-mobile-whatsapp-button"
                                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#E2BA03] text-[#472500] font-semibold px-6 py-3.5"
                            >
                                <MessageCircle size={18} />
                                WhatsApp Us to Order
                            </a>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </motion.header>
    );
};

export default Navbar;
